import { parseDate } from '../helpers/parse-date.js';
import isFalsy from '../helpers/is-falsy.js';
import isTruthy from '../helpers/is-truthy.js';

const STATUS_IDS = ['not_submitted', 'pending_approval', 'approved'];

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';
const isBoolean = (value) => isBlank(value) || isTruthy(value) || isFalsy(value);

// A row with a Time Off Type is time off, so it carries no client or project.
const isTimeOff = (row) => !isBlank(row?.['Time Off Type']);

const timeEntries = {
  'Import ID': {
    requirementType: 'required',
    renamedFrom: 'ID',
    uniqueInFile: true,
    description:
      'Unique identifier for the imported time entry. It must be unique within the file, and rows are also checked against previous imports. If a duplicate is found, the row will be skipped from import. Previously named "ID".',
    dataType: 'string',
    validator: (value) => !isBlank(value),
  },
  Date: {
    requirementType: 'required',
    description: 'Date of the time entry in "YYYY-MM-DD" or "MM/DD/YYYY" format.',
    dataType: 'date',
    validator: (value) => !!parseDate(value),
  },
  'Member Email': {
    requirementType: 'required',
    description:
      "Email address of the member for the time entry. It must match the member's email as it appears in the member's workspace profile.",
    dataType: 'email',
    validator: (value) => !isBlank(value) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim()),
  },
  'Client Name': {
    requirementType: 'optional',
    description:
      'The name of the client associated with the time entry. Required unless "Time Off Type" is provided. This field must match the name of a client exactly, including spaces, punctuation and capitalization, otherwise the time entry will be skipped during import.',
    dataType: 'string',
    validator: (value, row) => isTimeOff(row) || !isBlank(value),
  },
  'Project Name': {
    requirementType: 'optional',
    description:
      'The name of the project associated with the time entry. Required unless "Time Off Type" is provided. This field must match the name of a project exactly, including spaces, punctuation and capitalization, otherwise the time entry will be skipped during import.',
    dataType: 'string',
    validator: (value, row) => isTimeOff(row) || !isBlank(value),
  },
  'Role Name': {
    requirementType: 'optional',
    description:
      "If the time entry is associated with a project role (i.e. 'Senior Project Manager') you may include it here. If the role does not exist on the project, it will be automatically created. Ignored when the project does not use roles.",
    dataType: 'string',
    validator: () => true,
  },
  'Task Name': {
    requirementType: 'optional',
    description:
      'If the time entry is associated with a task (i.e. \'Build homepage banner design\') you may include it here. If the task does not exist on the project, it will be automatically created. On a project that uses roles, the task is assigned to the role from "Role Name" (no assignment is made when the row has no role); on a project that does not use roles, the task is assigned to the member.',
    dataType: 'string',
    validator: () => true,
  },
  Minutes: {
    requirementType: 'required',
    description: 'The duration of the time entry in minutes.',
    dataType: 'number',
    validator: (value) => {
      if (isBlank(value)) return false;
      const minutes = Number(String(value).trim());
      // The minutes column is a 32-bit integer, so anything larger fails on insert.
      return !isNaN(minutes) && minutes > 0 && minutes <= 2147483647;
    },
  },
  Notes: {
    requirementType: 'optional',
    description: 'Provide some description or notes related to the time entry.',
    dataType: 'string',
    validator: () => true,
  },
  'Time Off Type': {
    requirementType: 'optional',
    description:
      'Leave this blank otherwise the time entry will be treated as Time Off. If there is a value it must match one of the time off types listed on the "Settings - Custom Data - Time Off Types" page.',
    dataType: 'string',
    validator: () => true,
    warning: (value, row) =>
      isTimeOff(row) && (!isBlank(row?.['Client Name']) || !isBlank(row?.['Project Name']))
        ? 'The row will import as time off, so its "Client Name" and "Project Name" will be ignored.'
        : null,
  },
  Billable: {
    requirementType: 'optional',
    description:
      'Provide "Yes" (or "Y", "True", "1") if billable, otherwise the time entry will be treated as non-billable.',
    dataType: 'boolean',
    validator: isBoolean,
  },
  Rate: {
    requirementType: 'optional',
    description:
      'Hourly rate should be a numeric value, and will be associated with the Role if the role is created via this import. Otherwise this field will be ignored.',
    dataType: 'number',
    validator: (value) => isBlank(value) || !isNaN(Number(String(value).trim())),
  },
  Status: {
    requirementType: 'optional',
    description: 'One of: not_submitted, pending_approval, approved. Defaults to not_submitted when blank.',
    dataType: 'string',
    validator: (value) => isBlank(value) || STATUS_IDS.includes(String(value).trim()),
  },
  Invoiced: {
    requirementType: 'optional',
    description:
      'Provide "Yes" (or "Y", "True", "1") if the time entry has been invoiced in another system or prior to running this import. By default all billable and imported time entries will be considered ready for invoicing.',
    dataType: 'boolean',
    validator: isBoolean,
  },
};

export default timeEntries;
